/**
 * loading: nothing has arrived yet.
 * updating: a refresh is in flight.
 * live: everything loaded, recently.
 * stale: it loaded once, but not for a while (a hidden tab, a slow network).
 * partial: the latest refresh failed for some of what is on screen, so part of it is older than the rest.
 * offline: the last refresh failed outright.
 *
 * The label an operator reads is derived from this, never fixed: "Systems live" used to be
 * printed whether or not anything had loaded.
 */
export type Freshness = "loading" | "updating" | "live" | "stale" | "partial" | "offline";

export const REFRESH_INTERVAL_MS = 30_000;
/** Three missed refreshes: past this, the screen says its data is old. */
export const STALE_AFTER_MS = 90_000;

export function deriveFreshness(input: {
  hasData: boolean;
  refreshing: boolean;
  allFailed: boolean;
  /** The latest refresh failed for at least one of the things on screen. */
  anyFailed: boolean;
  lastOkAt: number | null;
  now: number;
}): Freshness {
  if (input.allFailed) return "offline";
  if (!input.hasData) return input.refreshing ? "loading" : "offline";
  if (input.refreshing) return "updating";
  if (input.anyFailed) return "partial";
  if (input.lastOkAt === null || input.now - input.lastOkAt > STALE_AFTER_MS) return "stale";
  return "live";
}
